import { clearFiltersButtonText } from 'owa-locstrings/lib/strings/clearfiltersbuttontext.locstring.json';
import { toDateLabel } from 'owa-locstrings/lib/strings/todatelabel.locstring.json';
import { datePlaceholder } from 'owa-locstrings/lib/strings/dateplaceholder.locstring.json';
import { fromDateLabel } from 'owa-locstrings/lib/strings/fromdatelabel.locstring.json';
import { attachmentsLabel } from 'owa-locstrings/lib/strings/attachmentslabel.locstring.json';
import loc from 'owa-localize';
import { RefinerType } from '../store/schema/RefinerType';
import ScopeOption from '../store/schema/ScopeOption';
import { getStore } from '../store/store';
import getSearchScopeDisplayName from '../utils/getSearchScopeDisplayName';
import { observer } from 'mobx-react';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { DirectionalHint } from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { FocusTrapZone } from 'office-ui-fabric-react/lib/FocusTrapZone';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { List } from 'office-ui-fabric-react/lib/List';
import { Toggle } from 'office-ui-fabric-react/lib/Toggle';
import { ControlIcons } from 'owa-control-icons';
import { DropdownDatePicker } from 'owa-datepicker';
import { compareSearchScope, getKey, SearchScope } from 'owa-search-service';

import * as React from 'react';
import {
    onClearRefinersButtonClicked,
    onRefinerChanged,
    onScopeRefinerChanged,
    setFromDate,
    setIncludeAttachments,
    setRefinersVisibility,
    setToDate,
} from '../actions/internalActions';
import { today, OwaDate } from 'owa-datetime';

const styles = require('./SearchRefinersCallout.scss');

export interface SearchRefinersCalloutProps {
    showAttachmentRefiner: boolean;
    target: HTMLElement; // Mount point for the callout
}

@observer
export default class SearchRefinersCallout extends React.Component<SearchRefinersCalloutProps, {}> {
    render() {
        const store = getStore();
        const { includeAttachments } = store;

        const toDate = store.toDate;
        const fromDate = store.fromDate;
        const todayDate = today();

        return (
            <div
                onClick={this.onSearchRefinersCalloutClick}
                onKeyDown={this.onSearchRefinersKeyDown}>
                <Callout
                    directionalHint={DirectionalHint.bottomRightEdge}
                    directionalHintFixed={true}
                    gapSpace={1}
                    isBeakVisible={false}
                    onDismiss={this.onCalloutDismiss}
                    setInitialFocus={true}
                    target={this.props.target}>
                    <FocusTrapZone isClickableOutsideFocusTrap={true}>
                        {/* The list of folders to select the scope of the search. */}
                        <FocusZone>
                            <List
                                className={styles.folderOptions}
                                items={this.getScopeOptions()}
                                onRenderCell={this.renderScopeOption}
                            />
                        </FocusZone>

                        {/* Checkbox that determines if results should have attachments. */}
                        {this.props.showAttachmentRefiner && (
                            <div className={styles.attachmentsRefinerContainer}>
                                <Toggle
                                    label={loc(attachmentsLabel)}
                                    checked={includeAttachments}
                                    onChanged={this.onAttachmentsToggleToggled}
                                    styles={{
                                        root: {
                                            alignItems: 'center',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '0',
                                            width: '100%',
                                        },
                                    }}
                                />
                            </div>
                        )}

                        {/* Input to determine earliest date which email was received to be returned
                    as part of result set. */}
                        <div className={styles.dateRefinersContainer}>
                            <div className={styles.dateRefinerContainer}>
                                <DropdownDatePicker
                                    allowTextInput={false}
                                    className={styles.datePicker}
                                    disableAutoFocus={true}
                                    label={loc(fromDateLabel)}
                                    maxDate={toDate || todayDate}
                                    onSelectDate={this.onFromDateChanged}
                                    placeholder={loc(datePlaceholder)}
                                    value={fromDate}
                                />
                            </div>

                            {/* Input to determine latest date which email was received to be returned
                        as part of result set. */}
                            <div className={styles.dateRefinerContainer}>
                                <DropdownDatePicker
                                    allowTextInput={false}
                                    className={styles.datePicker}
                                    disableAutoFocus={true}
                                    label={loc(toDateLabel)}
                                    maxDate={todayDate}
                                    minDate={fromDate}
                                    onSelectDate={this.onToDateChanged}
                                    placeholder={loc(datePlaceholder)}
                                    value={toDate}
                                />
                            </div>
                        </div>

                        {/* Call to action button for this component ("Clear filters"). */}
                        <Link
                            className={styles.clearFiltersButton}
                            onClick={this.onClearFiltersClicked}>
                            {loc(clearFiltersButtonText)}
                        </Link>
                    </FocusTrapZone>
                </Callout>
            </div>
        );
    }

    // This is necessary because of React portals bubbling events up to the parent.
    private onSearchRefinersCalloutClick(evt: React.MouseEvent<unknown>) {
        evt.stopPropagation();
    }

    // This is necessary because of React portals bubbling events up to the parent.
    private onSearchRefinersKeyDown(evt: React.KeyboardEvent<HTMLElement>) {
        evt.stopPropagation();
    }

    /**
     * Getter to get the folder options that are available in the "Folders"
     * dropdown that determines the scope of the search.
     *
     * Entries include:
     * - All folders
     * - Inbox
     * - Deleted Items
     * - Sent Items
     * - Archive (if user has it configured)
     * - The folder search was initiated from (if not one of the above)
     */
    private getScopeOptions(): ScopeOption[] {
        const searchStore = getStore();
        const currentSearchScope = searchStore.staticSearchScope;
        const searchScopes: SearchScope[] = searchStore.staticSearchScopeList.slice();

        // Add the current folder scope (if not there already).
        let searchResultArray = searchScopes.filter(searchScope =>
            compareSearchScope(searchScope, currentSearchScope)
        );
        if (searchResultArray.length <= 0) {
            searchScopes.push(currentSearchScope);
        }

        // Add the initial folder scope (if not there already).
        const initialSearchScope = searchStore.initialSearchScope;
        searchResultArray = searchScopes.filter(searchScope =>
            compareSearchScope(searchScope, initialSearchScope)
        );
        if (searchResultArray.length <= 0) {
            searchScopes.push(initialSearchScope);
        }

        // Create the dropdown menu items.
        const items: ScopeOption[] = [];
        for (const searchScope of searchScopes) {
            items.push({
                key: getKey(searchScope),
                text: getSearchScopeDisplayName(searchScope),
                searchScope: searchScope,
            });
        }

        return items;
    }

    // Renders a scope option to select to change the scope of the search.
    private renderScopeOption = (option: ScopeOption): JSX.Element => {
        const isSelected = option.key === getKey(getStore().staticSearchScope);

        return (
            <button
                className={`${styles.folderOption} ${
                    isSelected ? styles.isSelected : styles.isNotSelected
                }`}
                onClick={this.onScopeOptionClicked(option)}
                data-is-focusable={true}
                aria-selected={isSelected}>
                {isSelected && (
                    <Icon
                        className={styles.selectedFolderCheckMark}
                        iconName={ControlIcons.CheckMark}
                    />
                )}
                <span>{option.text}</span>
            </button>
        );
    };

    /**
     * WI: 47711
     * This doesn't technically remove the lambda, but this component has
     * already been replaced. The flight controlling it is set to be graduated
     * in the next month or so, so sticking with this (as the component doesn't
     * get rendered anyway) in lieu of creating a new sub-component.
     */
    private onScopeOptionClicked(option: ScopeOption) {
        return () => {
            onScopeRefinerChanged(option);
        };
    }

    // Handler when "Clear filters" button is pressed.
    private onClearFiltersClicked(evt: React.MouseEvent<unknown>) {
        evt.stopPropagation();
        onClearRefinersButtonClicked();
    }

    // Handler when from date refiner is changed.
    private onFromDateChanged(date: OwaDate) {
        setFromDate(date);
        onRefinerChanged(RefinerType.fromDate);
    }

    // Handler when to date refiner is changed.
    private onToDateChanged(date: OwaDate) {
        setToDate(date);
        onRefinerChanged(RefinerType.toDate);
    }

    // Handler when attachments refiner is changed.
    private onAttachmentsToggleToggled(checked: boolean) {
        setIncludeAttachments(checked);
        onRefinerChanged(RefinerType.includeAttachments);
    }

    // Dismiss handler when Fabric Callout thinks it should be dismissed.
    private onCalloutDismiss(evt: React.MouseEvent<unknown>) {
        evt.stopPropagation();

        /**
         * When new search results render, the mail list attempts to steal focus,
         * so don't allow callout to hide in that scenario.
         */
        if (evt.type !== 'focus') {
            setRefinersVisibility(false /* isVisible */);
        }
    }
}
